/**
 * CSRF対策ユーティリティ
 *
 * @file csrf.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * CSRFトークンの取得・保存・検証を管理。
 * Double Submit Cookie Patternを採用し、全POST/PUT/DELETEリクエストにトークンを付与。
 */

const CSRF_TOKEN_COOKIE_NAME = 'csrf_token';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * バックエンドからCSRFトークンを取得（リトライロジック付き）
 *
 * @param role - ユーザーロール（buyer, seller, admin）。必須パラメータ。
 */
export async function fetchCsrfToken(role: 'buyer' | 'seller' | 'admin'): Promise<string> {
  if (!role) {
    throw new Error('[CSRF] fetchCsrfToken: role parameter is required');
  }
  const maxRetries = 3;
  const retryDelay = 1000; // 1秒

  // ロールに応じたエンドポイントを決定
  const csrfEndpoint = `${API_BASE_URL}/api/v1/${role}/auth/csrf-token`;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[CSRF] トークン取得試行 ${attempt}/${maxRetries} (role: ${role})`);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒タイムアウト

      const response = await fetch(csrfEndpoint, {
        credentials: 'include', // Cookieを送受信
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // レスポンスボディの詳細エラーメッセージを取得
        let errorDetail = '';
        try {
          const errorData = await response.json();
          errorDetail = errorData?.detail || errorData?.message || '';
        } catch {
          // JSON解析失敗時は空文字
        }

        const errorMessage = errorDetail
          ? `CSRFトークンの取得に失敗しました: ${errorDetail}`
          : `CSRFトークンの取得に失敗しました (HTTP ${response.status})`;

        console.error(`[CSRF] レスポンスエラー: status=${response.status}, detail=${errorDetail}`);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const token = data.csrf_token;

      if (!token) {
        throw new Error('CSRFトークンがレスポンスに含まれていません');
      }

      console.log(`[CSRF] トークン取得成功（試行 ${attempt}/${maxRetries}）`);
      return token;
    } catch (error) {
      console.error(`[CSRF] トークン取得エラー（試行 ${attempt}/${maxRetries}）:`, error);

      if (attempt === maxRetries) {
        // 最終試行で失敗した場合のみエラーをスロー
        throw new Error('CSRFトークンの取得に失敗しました（3回リトライ後）');
      }

      // リトライ前に待機
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  // TypeScript の型チェックを通すため（実際には到達しない）
  throw new Error('CSRFトークンの取得に失敗しました');
}

/**
 * CookieからCSRFトークンを取得
 */
export function getCsrfTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_TOKEN_COOKIE_NAME) {
      return value;
    }
  }
  return null;
}

/**
 * CSRFトークンを取得（Cookie優先、なければAPI呼び出し）
 *
 * @param role - ユーザーロール（buyer, seller, admin）。必須パラメータ。
 */
export async function getCsrfToken(role: 'buyer' | 'seller' | 'admin'): Promise<string> {
  if (!role) {
    throw new Error('[CSRF] getCsrfToken: role parameter is required');
  }

  // 1. Cookieから取得を試みる
  const token = getCsrfTokenFromCookie();
  if (token) {
    return token;
  }

  // 2. APIから新規取得
  return await fetchCsrfToken(role);
}

/**
 * CSRFトークンをクリア
 * Cookieはバックエンドが管理するため、クライアント側では削除しない
 */
export function clearCsrfToken(): void {
  // Cookie管理はバックエンドで実施
}
